
import Vue from "vue";
import { mapState } from "vuex";
import { download } from "@/utils";
import titleEyeIcon from "@/assets/eye.png";
import {
  BATTLEYE_R2_NEW_EN_FLOW,
  BATTLEYE_R2_NEW_EN_HERO,
  BattleyeR2NewEnFlowItem
} from "@/constants/battleye-r2-new/enCopy";
import {
  BATTLEYE_R2_NEW_EN_USE_OSS,
  resolveBattleyeR2NewEnAsset
} from "@/constants/battleye-r2-new/enAssets";

type BattleyeR2NewEnTextItem = Extract<
  BattleyeR2NewEnFlowItem,
  { type: "text" }
>;
type BattleyeR2NewEnImageItem = Extract<
  BattleyeR2NewEnFlowItem,
  { type: "image" }
>;
type BattleyeR2NewEnRenderBlock =
  | BattleyeR2NewEnTextItem
  | {
      id: string;
      type: "imageGroup";
      images: BattleyeR2NewEnImageItem[];
    };

const isRawSpecRow = (item: BattleyeR2NewEnFlowItem) => {
  if (item.type !== "text") {
    return false;
  }

  const match = item.id.match(/^p15-t(\d+)$/);
  if (!match) {
    return false;
  }

  const index = Number(match[1]);
  return index >= 2 && index <= 19;
};

const isRawPackageRow = (item: BattleyeR2NewEnFlowItem) =>
  item.type === "text" &&
  (item.id === "p15-t22" || /^p16-t0[1-5]$/.test(item.id));

const buildRenderBlocks = (): BattleyeR2NewEnRenderBlock[] => {
  const blocks: BattleyeR2NewEnRenderBlock[] = [];
  let images: BattleyeR2NewEnImageItem[] = [];

  const flushImages = () => {
    if (!images.length) {
      return;
    }

    blocks.push({
      id: images.map(image => image.id).join("__"),
      type: "imageGroup",
      images
    });
    images = [];
  };

  BATTLEYE_R2_NEW_EN_FLOW.forEach(item => {
    if (isRawSpecRow(item) || isRawPackageRow(item)) {
      return;
    }

    if (item.type === "image") {
      images.push(item);
      return;
    }

    flushImages();
    blocks.push(item);
  });
  flushImages();

  return blocks;
};

const RENDER_BLOCKS = buildRenderBlocks();
const TYPING_SOUND_VIDEO =
  "https://cdn-www.dry---studio.com/battleye_r2/battleye_r2_08_typing_sound.mp4";
const JXG_IMAGE_BLOCK_ID = "p04-i01__p04-i02";
const P10_DESKTOP_IMAGE = "/detail/battleye-r2-new-en/battleye_r2_new_10_pc.png";
const SPEC_HTML =
  '<div class="spec-head spec-head--blank"></div><div class="spec-head">Carbon Silver (Black)</div><div class="spec-head">Damascus (White)</div><div class="spec-label">Keyboard Body</div><div class="spec-value">Aluminum Alloy<br />Carbon fiber components with pure silver accents</div><div class="spec-value">Aluminum Alloy<br />Damascus patterned components</div><div class="spec-label">Finish</div><div class="spec-value spec-value--span-2">Anodizing</div><div class="spec-label">Touchscreen</div><div class="spec-value spec-value--span-2">1.7-Inch 60Hz High-Res LCD Touchscreen<br />Instant Mode-Switching: Battle, Valorant, Typing, Custom<br />On-Screen Monitoring: Dynamic RT Actuation, HE Advanced Settings, Customizable Nameplate</div><div class="spec-label">RT Accuracy</div><div class="spec-value spec-value--span-2">0.001 mm</div><div class="spec-label">Switches</div><div class="spec-value spec-value--span-2">WS Diamond HE Switches (Angry Miao Custom Edition)</div><div class="spec-label">Plate</div><div class="spec-value spec-value--span-2">Carbon Fiber</div><div class="spec-label">Keycaps</div><div class="spec-value">Dark translucent PC</div><div class="spec-value">Clear translucent PC</div><div class="spec-label">Polling Rate</div><div class="spec-value spec-value--span-2">8K</div><div class="spec-label">Scan Rate</div><div class="spec-value spec-value--span-2">Single Key 256K, All Keys 32K</div><div class="spec-label">Latency</div><div class="spec-value spec-value--span-2">0.1 ms</div><div class="spec-label">Connectivity</div><div class="spec-value spec-value--span-2">USB Type-C, wired-mode only</div><div class="spec-label">Lighting Effects</div><div class="spec-value spec-value--span-2">Customizable in-switch RGB and under-screen lighting effects</div><div class="spec-label">Software</div><div class="spec-value spec-value--span-2">Driverless Web Interface (Real-time mapping / Snap Tap / SOCD / Rappy Snappy / DKS / Macros)</div>';
const PACKAGE_HTML =
  "<div>Keyboard</div><div>AngryMiao Keyboard Sticker</div><div>Dry Studio Keyboard Sticker</div><div>USB C to C Cable</div><div>USB C to A Adapter</div><div>Quick Start Guide</div><div>4 Extra Keycaps (W, A, S, D)</div>";

export default Vue.extend({
  name: "BattleyeR2NewEn",
  data(): {
    useOss: boolean;
    titleEyeIcon: string;
    hero: typeof BATTLEYE_R2_NEW_EN_HERO;
    packageHtml: string;
    renderBlocks: BattleyeR2NewEnRenderBlock[];
    specHtml: string;
    typingSoundVideo: string;
  } {
    return {
      useOss: BATTLEYE_R2_NEW_EN_USE_OSS,
      titleEyeIcon,
      hero: BATTLEYE_R2_NEW_EN_HERO,
      packageHtml: PACKAGE_HTML,
      renderBlocks: RENDER_BLOCKS,
      specHtml: SPEC_HTML,
      typingSoundVideo: TYPING_SOUND_VIDEO
    };
  },
  computed: {
    ...mapState("app", {
      isMobile: "isMobile",
      oss: "oss"
    })
  },
  methods: {
    onDownload(url: string) {
      download(url);
    },
    onOpen(url: string) {
      window.open(url, "_blank");
    },
    assetPath(assetPath: string) {
      return resolveBattleyeR2NewEnAsset({
        assetPath,
        oss: this.oss,
        useOss: this.useOss
      });
    },
    formatHtml(html: string) {
      const formatted = html.replace(
        /(?:<br\s*\/?>\s*){2,}/gi,
        '<span class="detail-paragraph-gap"></span>'
      );

      if (this.isMobile) {
        return formatted;
      }

      return formatted.replace(/<br\s*\/?>/gi, " ");
    },
    formatTitleHtml(html: string) {
      return this.formatHtml(html);
    },
    imageGroupClass(
      block: Extract<BattleyeR2NewEnRenderBlock, { type: "imageGroup" }>
    ) {
      const count = this.displayImages(block).length;
      if (block.id === JXG_IMAGE_BLOCK_ID) {
        return "layout-single";
      }

      if (count >= 4) {
        return "layout-grid-4";
      }
      if (count === 3) {
        return "layout-grid-3";
      }
      if (count === 2) {
        return "layout-grid-2";
      }
      return "layout-single";
    },
    displayImages(
      block: Extract<BattleyeR2NewEnRenderBlock, { type: "imageGroup" }>
    ) {
      if (!this.isMobile && block.id === "p10-i01") {
        return block.images.map(image => ({
          ...image,
          src: this.assetPath(P10_DESKTOP_IMAGE),
          alt: "20260715-102212"
        }));
      }

      return block.images.map(image => ({
        ...image,
        src: this.assetPath(image.src)
      }));
    },
    textClass(block: BattleyeR2NewEnTextItem) {
      return {
        "detail-flow-text--body": block.role === "body",
        "detail-flow-text--subtitle": block.role === "subtitle",
        "detail-text--minor-title": block.role === "subtitle",
        "detail-flow-text--highlight": block.hasHighlight
      };
    },
    isTypingSoundBlock(block: BattleyeR2NewEnTextItem) {
      return block.html.includes("打字音纯享版");
    }
  }
});
